import { useCallback } from 'react';
import { useApiClient } from '../../../contexts/ApiContext';
import { toPartyConfig } from './rushMidAdapters';
import { toSnapshot, type SidePref } from './useRushMidMatch';

/**
 * Party-lobby command surface — the bounded context for the pre-match party (create /
 * join by code / config / leave / start). Each call hits the typed HTTP client and
 * returns the narrowed authoritative snapshot, applied inline by the caller.
 */
export function useParty() {
  const apiClient = useApiClient();

  const createParty = useCallback(
    async () => toSnapshot(await apiClient.api.rushMidControllerCreateParty()),
    [apiClient],
  );
  const joinParty = useCallback(
    async (code: string) => toSnapshot(await apiClient.api.rushMidControllerJoinParty({ code })),
    [apiClient],
  );
  const configParty = useCallback(
    async (mode: number, ticket: number, side: SidePref, matchMode?: 'ranked' | 'bots') =>
      toSnapshot(await apiClient.api.rushMidControllerConfigParty(toPartyConfig(mode, ticket, side, matchMode))),
    [apiClient],
  );
  const leaveParty = useCallback(
    async () => toSnapshot(await apiClient.api.rushMidControllerLeaveParty()),
    [apiClient],
  );
  const kickParty = useCallback(
    async (targetUserId: string) =>
      toSnapshot(await apiClient.api.rushMidControllerKickParty({ userId: targetUserId })),
    [apiClient],
  );
  const setReady = useCallback(
    async (ready: boolean) => toSnapshot(await apiClient.api.rushMidControllerSetPartyReady({ ready })),
    [apiClient],
  );

  return { createParty, joinParty, configParty, leaveParty, kickParty, setReady };
}
